import { useEffect, useState, useCallback } from 'preact/hooks';

/**
 * Linear tutorial state. Each step corresponds to a single tooltip pinned near
 * a specific control. `DONE` means the user has finished (or skipped) the
 * tour; `localStorage` is checked on mount so completing the tour on the
 * digital twin doesn't re-trigger it in AR (and vice versa).
 */
export enum OnboardingStep {
  TAP_BIRD = 'TAP_BIRD',
  TAP_NEXT = 'TAP_NEXT',
  TAP_FILTER = 'TAP_FILTER',
  RECORD = 'RECORD',
  DONE = 'DONE',
}

// Module-level singleton so AROverlay, DigitalTwinView and MicrositeShell all
// share the same step state within one page lifetime. Persistence across
// reloads is intentionally OFF for now — see TODO below — so the tour
// re-runs every page load.
let currentStep: OnboardingStep = OnboardingStep.TAP_BIRD;
const subscribers = new Set<(s: OnboardingStep) => void>();

function setStep(next: OnboardingStep): void {
  if (next === currentStep) return;
  currentStep = next;
  subscribers.forEach((fn) => fn(next));
}

// TODO: re-enable `localStorage` persistence (key `murmurations:onboarded`)
// once the tour content is locked. Disabled for development so dev/QA can
// re-trigger the tour on every reload without clearing storage.

const ORDER: readonly OnboardingStep[] = [
  OnboardingStep.TAP_BIRD,
  OnboardingStep.TAP_NEXT,
  OnboardingStep.TAP_FILTER,
  OnboardingStep.RECORD,
  OnboardingStep.DONE,
];

function nextOf(step: OnboardingStep): OnboardingStep {
  const i = ORDER.indexOf(step);
  if (i < 0 || i >= ORDER.length - 1) return OnboardingStep.DONE;
  return ORDER[i + 1];
}

export interface UseOnboarding {
  step: OnboardingStep;
  /**
   * Advance to the next step, but only if the current step matches `from`.
   * Guards against duplicate event firings and out-of-order triggers (e.g.
   * Next being clicked before the bird-tap step has been reached). Pass no
   * argument to advance unconditionally.
   */
  advance: (from?: OnboardingStep) => void;
  /** Skip the rest of the tour and mark onboarding complete. */
  skip: () => void;
  /** Reset to step one. Dev/debug — not wired to any UI. */
  reset: () => void;
}

export function useOnboarding(): UseOnboarding {
  const [step, setLocalStep] = useState<OnboardingStep>(currentStep);

  useEffect(() => {
    const onChange = (s: OnboardingStep) => setLocalStep(s);
    subscribers.add(onChange);
    // Re-sync in case the singleton changed between render and effect-run.
    if (currentStep !== step) setLocalStep(currentStep);
    return () => {
      subscribers.delete(onChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const advance = useCallback((from?: OnboardingStep) => {
    if (from !== undefined && currentStep !== from) return;
    setStep(nextOf(currentStep));
  }, []);

  const skip = useCallback(() => setStep(OnboardingStep.DONE), []);

  const reset = useCallback(() => {
    setStep(OnboardingStep.TAP_BIRD);
  }, []);

  return { step, advance, skip, reset };
}
